import React, { useContext, useEffect, useState } from 'react';
import { KeyValueView, CustomModal, AppContext, EMPTY_OBJECT, ConfirmationModal } from 'commons-ui';
import { Button, KIND } from 'baseui/button';
import useUserProfileActions from '../useUserProfileActions';
import useKycDetails from './useKycDetails';
import { makeActionHandler, makeActionList } from './actions';
import Form60Details from './form60Details';
import KycDocsHistory from './kycDocsHistory';
import { KYC_STATUS_VALUE, NON_TERMINAL_STATUSES, PAN_MESSAGE_CONSTANTS } from './constants';
import BankDetails from './kycAliasCommands/bankDetails';
import VpaDetails from './kycAliasCommands/vpaDetails';
import useService from './useService';
import { styles } from './KycDetails.style';

const KycDetails = () => {
	const { keyConfig, keyValueLayout, kycId, manualVerificationRequired, kycStatus } = useKycDetails();
	const { getActionHandler, getActionList } = useUserProfileActions(makeActionHandler, makeActionList, {
		kycId,
		manualVerificationRequired
	});
	const [panData, setPanData] = useState<Object>(EMPTY_OBJECT);
	const { getTdsDocData, manuallyLinkPan, canApprovePan } = useService();
	const { showAlert } = useContext(AppContext);
	const [showConfirmation, setShowConfirmation] = useState(false);

	const getPanData = async () => {
		const data = await getTdsDocData();
		setPanData(data);
	};

	const handlePanApproveConfirm = async () => {
		setShowConfirmation(false);
		const result = await manuallyLinkPan(panData?.kycId);
		if (result?.data?.success === false) {
			showAlert(result.data.error, { type: 'error' });
			return;
		}

		if (result?.data?.error) {
			showAlert(result.data.error, { type: 'error' });
			return;
		}

		if (result?.data?.message) {
			getPanData();
			showAlert(result.data.message, { type: 'success' });
		}
	};

	const handlePanApprove = () => {
		setShowConfirmation(true);
	};

	useEffect(() => {
		getPanData();
	}, []);

	return (
		<>
			{Object.keys(panData).length > 0 && (
				<div style={styles.mainContainer}>
					<div style={styles.contentContainer}>
						<div style={styles.section}>
							<div style={styles.kycDetailsLabel}>
								<div>Utility</div>
							</div>
							<div style={styles.kycDetailsValue}>
								<div>PAN Status</div>
							</div>
						</div>
						<div style={styles.section}>
							<div style={styles.kycDetailsLabel}>
								<div>Status</div>
							</div>
							<div style={styles.kycDetailsValue}>
								{panData?.status ? <div>{panData?.status} </div> : <div>NOT_INITIATED</div>}
							</div>
						</div>
						<div style={styles.section}>
							<div style={styles.kycDetailsLabel}>
								<div>Last Updated At</div>
							</div>
							<div style={styles.kycDetailsValue}>
								<div>{panData?.kyc_details?.lastUpdatedAt || panData?.kyc_details?.timeStamp || 'N/A'} </div>
							</div>
						</div>

						<div style={styles.section}>
							<div style={styles.kycDetailsLabel}>
								<div>Name</div>
							</div>
							<div style={styles.kycDetailsValue}>
								<div>{panData?.kyc_details?.fullName ? panData?.kyc_details?.fullName : 'N/A'}</div>
							</div>
						</div>
						{!NON_TERMINAL_STATUSES.includes(panData?.status) && (
							<div style={styles.section}>
								<div style={styles.kycDetailsLabel}>
									<div>Error Reason</div>
								</div>
								<div style={styles.kycDetailsValue}>
									<div>{panData?.kyc_details?.message}</div>
								</div>
							</div>
						)}
						{panData?.status && !NON_TERMINAL_STATUSES.includes(panData?.status) && canApprovePan && (
							<div style={styles.section}>
								<div style={styles.kycDetailsLabel}>
									<div>Action Button</div>
								</div>
								<div style={styles.kycDetailsValue}>
									<Button onClick={handlePanApprove} size='mini' kind={KIND.primary}>
										Approve
									</Button>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{showConfirmation && (
				<ConfirmationModal
					confirmationHeader={PAN_MESSAGE_CONSTANTS.confirmationHeader}
					confirmationMessage={PAN_MESSAGE_CONSTANTS.confirmationMessage}
					agreeLabel={PAN_MESSAGE_CONSTANTS.agreeLabel}
					closeLabel={PAN_MESSAGE_CONSTANTS.closeLabel}
					onClose={() => setShowConfirmation(false)}
					confirmationDialogOnAgree={handlePanApproveConfirm}
				/>
			)}

			<KeyValueView
				keyConfig={keyConfig}
				keyValueLayout={keyValueLayout}
				getActionHandler={getActionHandler}
				getActionList={getActionList}
			/>
			{kycStatus !== KYC_STATUS_VALUE.NOT_INITIATED && <KycDocsHistory />}
			{kycStatus === KYC_STATUS_VALUE.APPROVED && <Form60Details />}
			{kycStatus === KYC_STATUS_VALUE.APPROVED && <BankDetails />}
			{kycStatus === KYC_STATUS_VALUE.APPROVED && <VpaDetails />}
			<CustomModal />
		</>
	);
};

export default KycDetails;
